import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef,
  OnInit,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  ValidatorFn,
  AbstractControl,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';

export function descriptionValidator(
  minLength: number = 0,
  maxLength: number = 500
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;

    // Allow empty strings (skip validation if value is empty)
    if (value === '') {
      return null;
    }

    if (typeof value !== 'string') {
      return { invalidType: 'Value must be a string' };
    }

    if (value.length < minLength || value.length > maxLength) {
      return {
        lengthError: `Description must be between ${minLength} and ${maxLength} characters long`,
      };
    }

    const injectionPattern = /<script|javascript:|onload=|onerror=|<\/script>/i;
    if (injectionPattern.test(value)) {
      return {
        injectionError: 'Description contains potentially harmful content',
      };
    }

    return null;
  };
}

type TemplateType = 'renameList' | 'addNewList' | 'shareList' | 'deleteList';

@Component({
  selector: 'app-lists-dialogs',
  templateUrl: './lists-dialogs.component.html',
  styleUrls: ['./lists-dialogs.component.scss'],
})
export class ListsDialogsComponent implements OnInit {
  @Input() fullScreen: boolean = false;
  @Input() header: string = 'Dialog header';
  @Input() body: string = 'Dialog body';
  @Input() footer: string = 'Dialog footer';
  @Input() templateType: TemplateType = 'renameList'; // Ensure templateType is correctly typed
  @Input() errorMessage: string = ''; // Default error message
  @Output() closeDialog = new EventEmitter<void>();
  @Output() submitForm = new EventEmitter<{
    id: number;
    name: string;
    email: string;
    web_link_domain: string;
    description: string;
    editor_permission: boolean;
  }>();

  @ViewChild('addNewList', { static: true }) addNewList!: TemplateRef<any>;
  @ViewChild('renameList', { static: true }) renameList!: TemplateRef<any>;
  @ViewChild('shareList', { static: true }) shareList!: TemplateRef<any>;
  @ViewChild('deleteList', { static: true }) deleteList!: TemplateRef<any>;

  shareUrl = window.location.href;
  templates: { [key: string]: TemplateRef<any> } = {};
  formData: FormGroup;

  constructor(private fb: FormBuilder, private clipboard: Clipboard) {
    this.formData = this.fb.group({
      id: [0],
      name: [''],
      email: [''],
      web_link_domain: [''],
      description: [''],
      editor_permission: [false],
    });
  }

  ngOnInit() {
    this.templates = {
      renameList: this.renameList,
      addNewList: this.addNewList,
      shareList: this.shareList,
      deleteList: this.deleteList,
    };

    this.setValidators();
  }

  setValidators() {
    const templateValidators = {
      renameList: {
        name: [Validators.required, Validators.maxLength(100)],
        email: [],
        description: [],
        editor_permission: [],
      },
      addNewList: {
        name: [Validators.required, Validators.maxLength(100)],
        email: [],
        web_link_domain: [],
        description: [descriptionValidator()],
        editor_permission: [],
      },
      shareList: {
        name: [],
        email: [Validators.required],
        web_link_domain: [Validators.required],
        description: [],
        editor_permission: [Validators.required],
      },
      deleteList: {
        name: [],
        email: [],
        web_link_domain: [],
        description: [],
        editor_permission: [],
      },
    };

    const validators = templateValidators[this.templateType] || {};

    this.formData.setControl(
      'name',
      this.fb.control('', validators.name || [])
    );
    this.formData.setControl(
      'email',
      this.fb.control('', validators.email || [])
    );
    this.formData.setControl(
      'description',
      this.fb.control('', validators.description || [])
    );
    this.formData.setControl(
      'editor_permission',
      this.fb.control('', validators.editor_permission || [])
    );
  }

  get description() {
    return this.formData.get('description');
  }
  get email() {
    return this.formData.get('email');
  }

  get name() {
    return this.formData.get('name');
  }

  onSubmit() {
    if (this.formData.valid) {
      this.submitForm.emit({
        id: 0,
        name: this.formData.value.name,
        email: this.formData.value.email,
        web_link_domain: this.formData.value.web_link_domain,
        description: this.formData.value.description,
        editor_permission: this.formData.value.editor_permission,
      });
    }
  }

  destroyMe() {
    this.closeDialog.emit();
  }
}
